import { createWebComponentInstances, dispatchCustomEvent, getMetaData } from '@helpers/functions';
import {
  deleteApiMyarjofavouriteextendId,
  deleteApiMyarjofavouriteId,
  FavoritePageItemModel,
  getApiMyarjofavouriteextendGet,
  getApiMyarjofavouriteGet,
  postApiMyarjofavouriteCreate,
  postApiMyarjofavouriteextendCreate,
} from '@_api/index';

import { globalCpmWebComponentName } from '../../web-components/globalCpm/_data';
import { WebComponent } from '@helpers/WebComponent';
import executionFunctionByRootNodeType from '@helpers/executionFunctionByRootNodeType';
import { RequestResponse } from '@_api/_base';

declare namespace arjoJs {
  function isDesktop(): boolean;
}

class CrossPortalMenu extends WebComponent {
  cpm: HTMLDivElement | null;

  constructor(rootNode: Document | ShadowRoot) {
    super(rootNode);
    this.cpm = this.rootNode.querySelector<HTMLDivElement>('.arjo-o-eshop-cross-portal-menu');

    window.addEventListener('resize', () => this.setWidth());
  }

  get shadowRootHost() {
    return (this.rootNode as ShadowRoot).host as HTMLElement;
  }

  initFavoriteFeature() {
    this.addEventFavorite();
    this.handleCheckFavoriteStatus();
  }

  initUserFeature() {
    this.addEventToAvatarMenuDropdownItem();
    this.addEventToAvatar();
  }

  initPrepare() {
    this.initPortalMenu();
    this.addEventToLogoutButton();
    this.setWidth();
    this.initFavoriteFeature();
    this.initUserFeature();
    this.addEventOutsideAccountMenuClick();
  }

  addEventToAvatar() {
    const avatarText = this.rootNode.querySelector('.arjo-o-eshop-cross-portal-menu__avatar-text');

    if (!avatarText) return;

    if (avatarText) {
      // While we need to re init this script base on external web component data, we need remove previous event listener on the element
      avatarText.removeEventListener('click', this.handleAvatarPortalMenuClick);

      avatarText.addEventListener('click', this.handleAvatarPortalMenuClick);
    }
  }

  handleAvatarPortalMenuClick(event: Event) {
    event.preventDefault();

    (event.currentTarget as HTMLElement).closest('.arjo-o-eshop-cross-portal-menu__avatar')?.classList.toggle('is-show');
  }

  handleAvatarMenuDropdownItemClick(event: Event) {
    event.preventDefault();
    const target = event.currentTarget as HTMLElement;

    if (!target) return;

    const href = target.getAttribute('href');
    const eventData = target.getAttribute('data-event-data');

    if (eventData) {
      dispatchCustomEvent({
        eventName: 'cpm-account',
        eventDict: {
          detail: {
            data: eventData,
          },
        },
      });
      return;
    }

    if (href) {
      const anchorTarget = target.getAttribute('target');

      if (anchorTarget === '_blank') {
        window.open(href, '_blank');
      } else {
        window.location.assign(href);
      }
    }
  }

  addEventToAvatarMenuDropdownItem() {
    const avatarDropdownMenu = this.rootNode.querySelector('.arjo-o-eshop-cross-portal-menu__avatar-dropdown');

    if (!avatarDropdownMenu) return;

    [...avatarDropdownMenu.querySelectorAll('a')].forEach((link) => {
      // While we need to re init this script base on external web component data, we need remove previous event listener on the element
      link.removeEventListener('click', this.handleAvatarMenuDropdownItemClick);

      link.addEventListener('click', this.handleAvatarMenuDropdownItemClick);
    });
  }

  addEventOutsideAccountMenuClick() {
    window.addEventListener('click', this.handleOutsideAccountMenuClick.bind(this));
  }

  handleOutsideAccountMenuClick(event: MouseEvent) {
    const elementClicked = event?.composedPath?.()?.[0] as HTMLElement;

    const avatarItem = this.rootNode.querySelector<HTMLDivElement>('.arjo-o-eshop-cross-portal-menu__avatar');

    if (avatarItem && !avatarItem.contains(elementClicked)) {
      this.rootNode.querySelector('.arjo-o-eshop-cross-portal-menu__avatar')?.classList?.remove('is-show');
    }
  }

  initPortalMenu() {
    const currentItem = this.rootNode.querySelector<HTMLElement>('.arjo-o-eshop-cross-portal-menu__item--current:not([data-event-attached="true"])');

    if (currentItem) {
      currentItem.dataset.eventAttached = 'true';
      // While we need to re init this script base on external web component data, we need remove previous event listener on the element
      currentItem.removeEventListener('click', (event) => this.handleClickPortalMenuMobile(event, this.rootNode));

      currentItem.addEventListener('click', (event) => this.handleClickPortalMenuMobile(event, this.rootNode));
    }
  }

  handleClickPortalMenuMobile(event: Event, rootNode: Document | ShadowRoot) {
    event.preventDefault();

    // Only local cpm will run this logic
    if (arjoJs.isDesktop() && rootNode instanceof Document) {
      return;
    }

    let parent = (event.target as HTMLElement).closest('.arjo-o-eshop-cross-portal-menu__left');

    if (!parent) {
      return;
    }

    parent.classList.toggle('is-show');
  }

  setWidth() {
    const crossPortalMenu = this.rootNode.querySelector<HTMLElement>('.arjo-o-eshop-cross-portal-menu');
    const header = this.rootNode.querySelector<HTMLElement>('.arjo-o-header');

    if (!header || !crossPortalMenu) {
      return;
    }

    crossPortalMenu?.style?.setProperty('--width', `${header.clientWidth}px`);

    // set height cpm
    header.style.setProperty('--height-cpm', `${crossPortalMenu.clientHeight}px`);
  }

  handleLogoutEvent(event: MouseEvent) {
    event.preventDefault();
    const target = event.currentTarget as HTMLElement;

    if (!target) return;

    const href = target.getAttribute('href');
    const eventData = target.getAttribute('data-event-data');

    if (eventData) {
      dispatchCustomEvent({
        eventName: 'cpm-log-out',
      });

      return;
    }

    if (href) {
      window.location.assign(href);
    }
  }

  addEventToLogoutButton() {
    const logoutButton = this.rootNode.querySelector<HTMLElement>('.arjo-o-eshop-cross-portal-menu__logout');

    if (!logoutButton) return;
    // While we need to re init this script base on external web component data, we need remove previous event listener on the element
    logoutButton.removeEventListener('click', this.handleLogoutEvent);

    logoutButton.addEventListener('click', this.handleLogoutEvent);
  }

  // Favorite page methods
  getFavoriteElements() {
    const favorite = this.rootNode.querySelector<HTMLElement>('.arjo-o-eshop-cross-portal-menu__add-favorite');
    const favoriteText = favorite?.querySelector('.arjo-o-eshop-cross-portal-menu__add-favorite-text');

    return { favorite, favoriteText };
  }

  addEventFavorite() {
    const { favorite } = this.getFavoriteElements();

    if (!favorite || favorite.dataset.eventAttached === 'true') return;

    // While we need to re init this script base on external web component data, we need remove previous event listener on the element
    favorite?.removeEventListener('click', this.handleFavoriteClick.bind(this));

    favorite?.addEventListener('click', this.handleFavoriteClick.bind(this));
    favorite.dataset.eventAttached = 'true';
  }

  handleFavoriteButtonClass(target: HTMLElement) {
    target.classList.toggle('disabled');
  }

  handleChangeFavoriteButtonAttributes(data?: { isFavorite: string; id: number }) {
    const { favorite } = this.getFavoriteElements();

    if (!favorite) return;

    if (data) {
      favorite.setAttribute('data-marked-as-favorite', data.isFavorite);
      favorite.setAttribute('data-favorite-id', data?.id?.toString());
    } else {
      favorite.removeAttribute('data-marked-as-favorite');
      favorite.removeAttribute('data-favorite-id');
    }
  }

  onGetFavoriteStatusSuccess(response: void | RequestResponse<FavoritePageItemModel>) {
    if (!response) return;

    const { favorite, favoriteText } = this.getFavoriteElements();

    const { data } = response;

    const { id } = data;

    if (!favorite || !id) return;

    this.handleChangeFavoriteButtonAttributes({ isFavorite: 'true', id });

    if (favoriteText) {
      favoriteText.innerHTML = favorite?.dataset.removeFromFavorite || '';
    }
  }

  handleCheckFavoriteStatus() {
    const url = window.location.href;
    const { favorite } = this.getFavoriteElements();

    if (!favorite) return;

    executionFunctionByRootNodeType({
      rootNode: this.rootNode,
      localCallback: () => {
        getApiMyarjofavouriteGet({ url })
          .then((response) => {
            this.onGetFavoriteStatusSuccess(response);
          })
          .catch(() => {
            this.handleChangeFavoriteButtonAttributes();
          });
      },
      webComponentCallback: () => {
        getApiMyarjofavouriteextendGet({ url })
          .then((response) => {
            this.onGetFavoriteStatusSuccess(response);
          })
          .catch(() => {
            this.handleChangeFavoriteButtonAttributes();
          });
      },
    });
  }

  onRemoveFavoriteSuccess(response: void | RequestResponse<boolean>, textAddFavorite: string | undefined, elementText: Element) {
    if (!response) return;

    const { success } = response;

    if (!success) return;

    this.handleChangeFavoriteButtonAttributes();

    if (textAddFavorite && elementText) {
      elementText.innerHTML = textAddFavorite;
    }

    window.favoriteTooltips?.forEach((favoriteTooltip) => favoriteTooltip.onChangeFavoriteState('remove'));
  }

  onCreateFavoriteSuccess(response: void | RequestResponse<FavoritePageItemModel>, textRemoveFavorite: string | undefined, elementText: Element) {
    if (!response) return;

    const { data, success } = response;

    if (!success) return;

    this.handleChangeFavoriteButtonAttributes({ isFavorite: 'true', id: data?.id as number });

    if (textRemoveFavorite && elementText) {
      elementText.innerHTML = textRemoveFavorite;
    }

    window.favoriteTooltips?.forEach((favoriteTooltip) => favoriteTooltip.onChangeFavoriteState('create'));
  }

  handleFavoriteClick() {
    const { favorite, favoriteText } = this.getFavoriteElements();

    if (!favoriteText || !favorite) return;

    const textAddFavorite = favorite.dataset.addToFavorite;
    const textRemoveFavorite = favorite.dataset.removeFromFavorite;

    const markedAsFavorite = favorite.dataset.markedAsFavorite;
    const rawUrl = window.location.href;
    const title = document.title;
    this.handleFavoriteButtonClass(favorite);
    const metaData = getMetaData();
    const portal = metaData?.sub_portal || metaData?.portal || null;

    if (markedAsFavorite) {
      const favoriteId = favorite.dataset.favoriteId;

      if (!favoriteId) return;

      executionFunctionByRootNodeType({
        rootNode: this.rootNode,
        localCallback: () => {
          deleteApiMyarjofavouriteId(+favoriteId)
            .then((response) => {
              this.onRemoveFavoriteSuccess(response, textAddFavorite, favoriteText);
            })
            .catch(console.error)
            .finally(() => this.handleFavoriteButtonClass(favorite));
        },
        webComponentCallback: () => {
          deleteApiMyarjofavouriteextendId(+favoriteId)
            .then((response) => {
              this.onRemoveFavoriteSuccess(response, textAddFavorite, favoriteText);
            })
            .catch(console.error)
            .finally(() => this.handleFavoriteButtonClass(favorite));
        },
      });
    } else {
      executionFunctionByRootNodeType({
        rootNode: this.rootNode,
        localCallback: () => {
          postApiMyarjofavouriteCreate({ rawUrl, title, portal })
            .then((response) => {
              this.onCreateFavoriteSuccess(response, textRemoveFavorite, favoriteText);
            })
            .catch(console.error)
            .finally(() => this.handleFavoriteButtonClass(favorite));
        },
        webComponentCallback: () => {
          postApiMyarjofavouriteextendCreate({ rawUrl, title, portal })
            .then((response) => {
              this.onCreateFavoriteSuccess(response, textRemoveFavorite, favoriteText);
            })
            .catch(console.error)
            .finally(() => this.handleFavoriteButtonClass(favorite));
        },
      });
    }
  }
}

const crossPortalMenus = createWebComponentInstances<CrossPortalMenu>(CrossPortalMenu, globalCpmWebComponentName);

// Web component only cannot access into class instance without window object
window.crossPortalMenus = crossPortalMenus;

window.addEventListener('DOMContentLoaded', () => {
  crossPortalMenus.forEach((menu) => {
    if (menu.rootNode instanceof Document) {
      menu.initPrepare();
    }
  });
});


export { CrossPortalMenu };
